import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import CelebrationOverlay from "@/components/CelebrationOverlay";

import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Worlds from "@/pages/Worlds";
import CategoryDetail from "@/pages/CategoryDetail";
import LessonPlayer from "@/pages/LessonPlayer";
import SimulationPlayer from "@/pages/SimulationPlayer";
import Coach from "@/pages/Coach";
import SkillTree from "@/pages/SkillTree";
import Challenges from "@/pages/Challenges";
import Progress from "@/pages/Progress";
import Search from "@/pages/Search";
import Premium from "@/pages/PremiumHub";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";

const Spinner = () => (
  <div className="min-h-screen grid place-items-center bg-background">
    <Loader2 className="animate-spin text-indigo-400" size={40} />
  </div>
);

function Protected({ children, requireOnboarded = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/" replace />;
  if (requireOnboarded && !user.onboarded && location.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Auth />} />
      <Route path="/onboarding" element={<Protected requireOnboarded={false}><Onboarding /></Protected>} />
      <Route path="/app" element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="worlds" element={<Worlds />} />
        <Route path="worlds/:key" element={<CategoryDetail />} />
        <Route path="coach" element={<Coach />} />
        <Route path="skill-tree" element={<SkillTree />} />
        <Route path="challenges" element={<Challenges />} />
        <Route path="progress" element={<Progress />} />
        <Route path="search" element={<Search />} />
        <Route path="premium" element={<Premium />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
      </Route>
      <Route path="/lesson/:id" element={<Protected><LessonPlayer /></Protected>} />
      <Route path="/simulation/:key" element={<Protected><SimulationPlayer /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <CelebrationOverlay />
            <Toaster position="top-center" theme="dark" richColors closeButton />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
